/**
 * Types for the Denker cursor state machine — cursor-led permissions and
 * the current desktop onboarding placeholder.
 *
 * Spec: docs/specs/2026-04-30-desktop-onboarding-design.md §3.2
 *       docs/specs/2026-05-04-cursor-led-permissions-design.md
 */
import type { PermissionState } from "./permissions-wizard-state";
import type { SceneId } from "./scenes/types";

export const FLY_DURATION_MS = 900;

/**
 * Pixel offset from the cursor element's top-left to the arrow's visual TIP.
 * The shared liquid arrow has its sharp point at ~(1.35,1.25) in its 14×18
 * viewBox. Callers compute a TARGET point and subtract this offset so
 * the rendered tip lands ON the target. (Previously {x:9,y:7} — the far-right
 * barb vertex — which made every cursor point ~8px up-left of its target.)
 */
export const CURSOR_TIP_OFFSET = { x: 1.35, y: 1.25 } as const;

export type CursorMode = "parked" | "flying" | "pointing" | "hidden";

export interface Position {
  x: number;
  y: number;
}

/** Short motion cues the controller can trigger on the cursor arrow.
 *  `null` is the resting state; `"nod"` and `"bounce"` map to CSS
 *  keyframes in `frontend/src/index.css` and auto-reset after the
 *  animation duration via `setAnim()`. */
export type CursorAnim = "nod" | "bounce" | null;

// Phases visible to the overlay router.
export type StepId =
  // Cursor-led intro sub-steps
  | "arrive"
  | "intro_fade"
  | "intro_greeting"
  | "intro_returning"
  // Cursor-led permission sub-steps
  | "perm_hotkeys"
  | "perm_voice"
  | "perm_voice_quality"
  | "perm_vision"
  | "complete";

export interface ControllerState {
  step: StepId;
  /** True while a scene-engine walker holds the stage. Owner-local;
   *  lets overlay effects defer to an in-flight walk instead of racing
   *  it for the bubble. */
  walkerActive: boolean;
  /** Denker's central cursor position. */
  position: Position;
  /** Display that owns `position` for cross-screen movements. Null means
   * render on the active display and interpret position in that window. */
  cursorDisplayId: number | null;
  mode: CursorMode;
  /** CSS animation name applied to the cursor arrow. */
  cursorAnim: CursorAnim;
  /** Opacity for the cursor (0–1). Used for fade-in/out. */
  cursorOpacity: number;
  /** Denker's central bubble (one global narrative line). */
  bubbleText: string | null;
  /** When true, bubble drives its own reveal timer instead of subscribing
   *  to TTS state. Retained for cross-webview broadcast compatibility —
   *  the bubble itself is now always self-driven (no TTS). */
  bubbleSelfDrive: boolean;
  /** Optional second line rendered below the main text. */
  bubbleHint: string | null;
  /** Action-surface reducer state for the current permission. Lives in the
   *  controller (not local useReducer) so it survives a monitor handoff —
   *  when the user crosses screens mid-grant, the new active webview
   *  picks up the in-progress state instead of resetting to idle. */
  permState: PermissionState;
  /** Owner-only. Set by the scene engine before each scene's play runs.
   *  Read by stepOnboardingDelta to compute Next/Prev. Null when no scene
   *  has played yet (fresh boot, post-restart before runIntro fires).
   *
   *  W2 note: although this field replicates across mirrored webviews via
   *  Zustand mirroredStore, no consumer outside the owner reads it. Future
   *  consumers MUST guard with getWindowRoleSync() === "owner" to avoid
   *  acting on a value that's a stale snapshot from a former owner. */
  currentSceneId: SceneId | null;
}
